// Fees & Revenue Settings Tab
import React, { useState, useMemo } from "react";
import SettingsSaveButtons from "./SettingsSaveButtons";

const FeesAndRevenue = ({
  settings,
  updateSettings,
  saveSettings,
  saving,
  hasUnsavedChanges,
  resetSettings,
}) => {
  const [previewPrice, setPreviewPrice] = useState(100);
  const ownerWithdrawalSettings = settings?.ownerWithdrawalSettings || {
    enabled: false,
    allowedRoles: ["R6"],
    fixedReserveAmount: 2000,
    reservePercent: 10,
    perRunCap: 5000,
    minimumWithdrawalAmount: 25,
    notesRequired: true,
  };

  // Format currency helper
  const formatCurrency = (amount) => {
    return amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Calculate revenue breakdown preview
  const preview = useMemo(() => {
    const price = parseFloat(previewPrice || 0);
    const stripeFee =
      (price * (settings.stripeFeePercent || 0)) / 100 +
      (settings.stripeFeeFixed || 0);
    const platformFeeProduct =
      (price * settings.platformFeePercentProduct) / 100;
    const shopCommission = (price * (settings.productShopPercent || 0)) / 100;
    const professionalRevenue =
      (price * (settings.productProfessionalPercent || 0)) / 100;
    const totalFees = stripeFee + platformFeeProduct;

    return {
      price,
      stripeFee,
      platformFee: platformFeeProduct,
      totalFees,
      shopCommission,
      professionalRevenue,
      netRevenue: price - totalFees - shopCommission,
    };
  }, [previewPrice, settings]);

  const handleSubmit = (e) => {
    e.preventDefault();
    saveSettings();
  };

  const updateOwnerWithdrawalSettings = (patch) => {
    updateSettings({
      ownerWithdrawalSettings: {
        ...ownerWithdrawalSettings,
        ...patch,
      },
    });
  };

  // Check validation
  const isInvalid =
    (settings.productProfessionalPercent || 0) +
      (settings.productShopPercent || 0) >
    97;

  return (
    <form onSubmit={handleSubmit}>
      {/* Payment Processing Fees Section */}
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "32px",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{ fontSize: "20px", fontWeight: "600", marginBottom: "24px" }}
        >
          💳 Payment Processing Fees
        </h2>

        <div style={{ marginBottom: "24px" }}>
          <label
            style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
          >
            Stripe Fee Percentage (%)
          </label>
          <input
            type="number"
            step="0.1"
            min="0"
            max="10"
            value={settings.stripeFeePercent ?? 2.9}
            onChange={(e) =>
              updateSettings({ stripeFeePercent: parseFloat(e.target.value) })
            }
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
            }}
            required
          />
          <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}>
            Stripe's standard rate is 2.9%
          </p>
        </div>

        <div style={{ marginBottom: "24px" }}>
          <label
            style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
          >
            Stripe Fixed Fee ($)
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            max="1"
            value={settings.stripeFeeFixed ?? 0.3}
            onChange={(e) =>
              updateSettings({ stripeFeeFixed: parseFloat(e.target.value) })
            }
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
            }}
            required
          />
          <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}>
            Stripe's standard fixed fee is $0.30 per transaction
          </p>
        </div>

        <div style={{ marginBottom: "0" }}>
          <label
            style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
          >
            Platform Fee - Products (%)
          </label>
          <input
            type="number"
            step="0.1"
            min="0"
            max="20"
            value={settings.platformFeePercentProduct}
            onChange={(e) =>
              updateSettings({
                platformFeePercentProduct: parseFloat(e.target.value),
              })
            }
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
            }}
            required
          />
          <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}>
            Platform fee for product sales (separate from Stripe fees)
          </p>
        </div>
      </div>

      {/* Platform Owner Withdrawal Controls */}
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "32px",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{ fontSize: "20px", fontWeight: "600", marginBottom: "12px" }}
        >
          🏦 Platform Owner Withdrawal Controls
        </h2>
        <p style={{ fontSize: "14px", color: "#4b5563", marginBottom: "20px" }}>
          This step is documented for the platform owner withdrawal flow. It is
          platform-controlled, manual only, and limited to portal admin access
          ({ownerWithdrawalSettings.allowedRoles?.join(", ") || "R6"}).
          See docs/platform-owner-withdrawal.md for the exact run steps and
          reporting expectations.
        </p>

        <div style={{ marginBottom: "20px" }}>
          <label
            style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
          >
            Enable Platform Owner Withdrawals
          </label>
          <input
            type="checkbox"
            checked={!!ownerWithdrawalSettings.enabled}
            onChange={(e) =>
              updateOwnerWithdrawalSettings({ enabled: e.target.checked })
            }
            style={{ transform: "scale(1.1)" }}
          />
          <p style={{ fontSize: "12px", color: "#6b7280", margin: "8px 0 0" }}>
            When enabled, portal admins can trigger manual owner withdrawals from
            the platform treasury screen.
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "20px",
          }}
        >
          <div>
            <label
              style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
            >
              Fixed Reserve Amount ($)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={ownerWithdrawalSettings.fixedReserveAmount ?? 2000}
              onChange={(e) =>
                updateOwnerWithdrawalSettings({
                  fixedReserveAmount: parseFloat(e.target.value) || 0,
                })
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>

          <div>
            <label
              style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
            >
              Reserve Percentage (%)
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={ownerWithdrawalSettings.reservePercent ?? 10}
              onChange={(e) =>
                updateOwnerWithdrawalSettings({
                  reservePercent: parseFloat(e.target.value) || 0,
                })
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>

          <div>
            <label
              style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
            >
              Per-Run Cap ($)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={ownerWithdrawalSettings.perRunCap ?? 5000}
              onChange={(e) =>
                updateOwnerWithdrawalSettings({
                  perRunCap: parseFloat(e.target.value) || 0,
                })
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>

          <div>
            <label
              style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
            >
              Minimum Withdrawal Amount ($)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={ownerWithdrawalSettings.minimumWithdrawalAmount ?? 25}
              onChange={(e) =>
                updateOwnerWithdrawalSettings({
                  minimumWithdrawalAmount: parseFloat(e.target.value) || 0,
                })
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>
        </div>

        <div style={{ marginTop: "20px" }}>
          <label style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}>
            Notes Required for Each Withdrawal
          </label>
          <input
            type="checkbox"
            checked={ownerWithdrawalSettings.notesRequired !== false}
            onChange={(e) =>
              updateOwnerWithdrawalSettings({ notesRequired: e.target.checked })
            }
            style={{ transform: "scale(1.1)" }}
          />
          <p style={{ fontSize: "12px", color: "#6b7280", margin: "8px 0 0" }}>
            Keep this on so every owner withdrawal has a documented reason for
            reporting and audit review.
          </p>
        </div>
      </div>

      {/* Revenue Distribution (Read-Only) */}
      {/* COMMENTED OUT: This section shows legacy commission structure that conflicts with new R3 product commission system.
          Shop owners configure R3 commissions (0-5%) at /dashboard/settings/booking instead.
          Platform takes 9% fee on all product sales (platformFeePercentProduct).
          
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "32px",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}
        >
          💵 Revenue Distribution (Managed by Shop Owners)
        </h2>
        <p style={{ color: "#6b7280", marginBottom: "24px", fontSize: "14px" }}>
          ⚠️ These settings are configured by individual shop owners in their
          dashboard. Portal admins cannot change these values.
        </p>

        <div style={{ marginBottom: "24px" }}>
          <label
            style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
          >
            Professional Revenue (%)
          </label>
          <input
            type="number"
            value={settings.productProfessionalPercent ?? 70}
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
              background: "#f9fafb",
              cursor: "not-allowed",
            }}
            disabled
          />
          <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}>
            Percentage of product price that goes to the professional
            (configured by shop owner)
          </p>
        </div>

        <div style={{ marginBottom: "0" }}>
          <label
            style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
          >
            Shop Commission (%)
          </label>
          <input
            type="number"
            value={settings.productShopPercent ?? 20}
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
              background: "#f9fafb",
              cursor: "not-allowed",
            }}
            disabled
          />
          <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}>
            Shop's commission on professional-owned products (configured by shop
            owner)
          </p>
        </div>

        {isInvalid && (
          <div
            style={{
              marginTop: "16px",
              padding: "12px",
              background: "#fee2e2",
              border: "1px solid #ef4444",
              borderRadius: "6px",
              color: "#991b1b",
              fontSize: "13px",
            }}
          >
            ⚠️ Combined Professional + Shop percentages cannot exceed 97% (need
            at least 3% for fees)
          </div>
        )}
      </div>
      */}

      {/* Revenue Calculator / Preview */}
      <div
        style={{
          background: "#eff6ff",
          border: "2px solid #3b82f6",
          borderRadius: "12px",
          padding: "24px",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{
            fontSize: "18px",
            fontWeight: "600",
            marginBottom: "16px",
            color: "#1e40af",
          }}
        >
          📊 Revenue Preview
        </h2>

        <div style={{ marginBottom: "20px" }}>
          <label
            style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
          >
            Sample Product Price ($)
          </label>
          <input
            type="number"
            step="0.01"
            min="1"
            value={previewPrice}
            onChange={(e) => setPreviewPrice(parseFloat(e.target.value) || 100)}
            style={{
              width: "200px",
              padding: "10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
            }}
          />
        </div>

        <div
          style={{
            background: "white",
            padding: "20px",
            borderRadius: "8px",
            border: "1px solid #e5e7eb",
          }}
        >
          <div style={{ marginBottom: "16px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "8px",
              }}
            >
              <span style={{ fontWeight: "600" }}>Product Price:</span>
              <span
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#059669",
                }}
              >
                ${formatCurrency(preview.price)}
              </span>
            </div>
          </div>

          <div
            style={{
              borderTop: "2px solid #e5e7eb",
              paddingTop: "12px",
              marginBottom: "12px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "6px",
                fontSize: "14px",
              }}
            >
              <span style={{ color: "#dc2626" }}>
                → Stripe Fee ({settings.stripeFeePercent || 0}% + $
                {settings.stripeFeeFixed || 0}
                ):
              </span>
              <span style={{ fontWeight: "600", color: "#dc2626" }}>
                -${formatCurrency(preview.stripeFee)}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "6px",
                fontSize: "14px",
              }}
            >
              <span style={{ color: "#dc2626" }}>
                → Platform Fee (Product{" "}
                {settings.platformFeePercentProduct || 0}%):
              </span>
              <span style={{ fontWeight: "600", color: "#dc2626" }}>
                -${formatCurrency(preview.platformFee)}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "6px",
                fontSize: "14px",
              }}
            >
              <span style={{ color: "#dc2626" }}>
                → Shop Commission ({settings.productShopPercent || 0}%):
              </span>
              <span style={{ fontWeight: "600", color: "#dc2626" }}>
                -${formatCurrency(preview.shopCommission)}
              </span>
            </div>
          </div>

          <div
            style={{
              borderTop: "2px solid #3b82f6",
              paddingTop: "12px",
              display: "flex",
              justifyContent: "space-between",
              fontSize: "16px",
            }}
          >
            <span style={{ fontWeight: "700", color: "#1e40af" }}>
              Professional Net Revenue:
            </span>
            <span
              style={{ fontSize: "20px", fontWeight: "700", color: "#059669" }}
            >
              ${formatCurrency(preview.netRevenue)}
            </span>
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "#6b7280",
              textAlign: "right",
              marginTop: "4px",
            }}
          >
            (
            {preview.price > 0
              ? ((preview.netRevenue / preview.price) * 100).toFixed(1)
              : 0}
            % of sale price)
          </div>
        </div>
      </div>

      {/* Save Buttons */}
      <SettingsSaveButtons
        onSave={handleSubmit}
        onCancel={resetSettings}
        saving={saving}
        hasChanges={hasUnsavedChanges}
      />

      {/* Info Box */}
      <div
        style={{
          marginTop: "24px",
          padding: "16px",
          background: "#eff6ff",
          border: "1px solid #3b82f6",
          borderRadius: "8px",
          fontSize: "13px",
          color: "#1e40af",
        }}
      >
        <strong>ℹ️ Important Notes:</strong>
        <ul style={{ margin: "8px 0 0", paddingLeft: "20px" }}>
          <li>
            Changes apply to all NEW products/services created after saving
          </li>
          <li>Existing items retain their original fee structure</li>
          <li>Stripe fees are in addition to platform fees</li>
          <li>Revenue distribution is managed by individual shop owners</li>
          <li>
            Platform owner withdrawal controls are managed in the section above
            and documented in docs/platform-owner-withdrawal.md
          </li>
        </ul>
      </div>
    </form>
  );
};

export default FeesAndRevenue;
